import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { 
    Bell, BellOff, BellRing, CheckCircle2, XCircle, 
    Moon, Dumbbell, Apple, Heart, Clock, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NANOUK_HAPPY = 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop';

const content = {
    de: {
        title: 'Push-Benachrichtigungen',
        subtitle: 'Nanouk hält dich auf dem Laufenden',
        enable: 'Aktivieren',
        disable: 'Deaktivieren',
        enabled: 'Aktiviert',
        disabled: 'Deaktiviert',
        permissionDenied: 'Benachrichtigungen wurden blockiert. Bitte erlaube sie in deinen Browser-Einstellungen.',
        subscribed: 'Du erhältst jetzt Benachrichtigungen von Nanouk!',
        unsubscribed: 'Benachrichtigungen deaktiviert.',
        categories: {
            title: 'Benachrichtigungstypen',
            motivation: {
                title: 'Tägliche Motivation',
                desc: 'Nanouk\'s morgendliche Grüße'
            },
            reminders: {
                title: 'Erinnerungen',
                desc: 'Schlaf, Wasser, Workouts'
            },
            achievements: {
                title: 'Erfolge',
                desc: 'Neue Level & Badges'
            },
            health: {
                title: 'Gesundheit',
                desc: 'Wichtige Alerts'
            }
        },
        notSupported: 'Dein Browser unterstützt keine Push-Benachrichtigungen.',
        notSupportedIOS: 'Um Push-Benachrichtigungen auf iOS zu erhalten, füge BESTBUDDY zu deinem Homescreen hinzu:',
        iosStep1: '1. Tippe auf das Teilen-Symbol (⎙)',
        iosStep2: '2. Wähle "Zum Home-Bildschirm"',
        iosStep3: '3. Öffne die App vom Homescreen',
        nanoukSays: 'Wuff! Mit Benachrichtigungen kann ich dich daran erinnern, genug zu trinken, gut zu schlafen und deine Ziele zu erreichen!'
    },
    en: {
        title: 'Push Notifications',
        subtitle: 'Let Nanouk keep you updated',
        enable: 'Enable',
        disable: 'Disable',
        enabled: 'Enabled',
        disabled: 'Disabled',
        permissionDenied: 'Notifications are blocked. Please allow them in your browser settings.',
        subscribed: 'You\'ll now receive notifications from Nanouk!',
        unsubscribed: 'Notifications disabled.',
        categories: {
            title: 'Notification Types',
            motivation: {
                title: 'Daily Motivation',
                desc: 'Nanouk\'s morning greetings'
            },
            reminders: {
                title: 'Reminders',
                desc: 'Sleep, water, workouts'
            },
            achievements: {
                title: 'Achievements',
                desc: 'New levels & badges'
            },
            health: {
                title: 'Health',
                desc: 'Important alerts'
            }
        },
        notSupported: 'Your browser doesn\'t support push notifications.',
        notSupportedIOS: 'To receive push notifications on iOS, add BESTBUDDY to your home screen:',
        iosStep1: '1. Tap the Share icon (⎙)',
        iosStep2: '2. Select "Add to Home Screen"',
        iosStep3: '3. Open the app from your home screen',
        nanoukSays: 'Woof! With notifications, I can remind you to drink enough water, sleep well, and reach your goals!'
    }
};

export default function PushNotifications({ language = 'de' }) {
    const t = content[language] || content.de;
    const [isSupported, setIsSupported] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [permission, setPermission] = useState('default');
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    
    const [categories, setCategories] = useState({
        motivation: true,
        reminders: true,
        achievements: true,
        health: true
    });
    
    useEffect(() => {
        // Detect iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(iOS);
        
        // Check if running as installed PWA
        const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
        setIsStandalone(standalone);
        
        // Check if push notifications are supported
        if ('Notification' in window && 'serviceWorker' in navigator) {
            setIsSupported(true);
            setPermission(Notification.permission);
            
            // Check if already subscribed
            checkSubscriptionStatus();
        }
    }, []);
    
    const checkSubscriptionStatus = async () => {
        try {
            const response = await axios.get(`${API}/notifications/status`);
            setIsSubscribed(response.data.subscribed);
        } catch (error) {
            console.error('Error checking push status:', error);
        }
    };
    
    const handleToggle = async () => {
        if (isSubscribed) {
            await unsubscribe();
        } else {
            await subscribe();
        }
    };
    
    const subscribe = async () => {
        setIsLoading(true);
        try {
            // Request permission
            const permission = await Notification.requestPermission();
            setPermission(permission);
            
            if (permission !== 'granted') {
                toast.error(t.permissionDenied);
                return;
            }
            
            // Register service worker if not already registered
            let registration = await navigator.serviceWorker.getRegistration();
            if (!registration) {
                registration = await navigator.serviceWorker.register('/sw.js');
            }
            await navigator.serviceWorker.ready;

            // Get VAPID public key (from env or backend)
            let vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
            if (!vapidKey) {
                try {
                    const r = await axios.get(`${API}/notifications/vapid-public-key`);
                    vapidKey = r.data?.public_key;
                } catch (e) { /* ignore */ }
            }
            if (!vapidKey) {
                toast.error(language === 'de' ? 'Push-Server nicht konfiguriert' : 'Push server not configured');
                return;
            }

            // Subscribe via PushManager
            let pushSub = await registration.pushManager.getSubscription();
            if (!pushSub) {
                const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
                const b64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
                const raw = window.atob(b64);
                const key = new Uint8Array(raw.length);
                for (let i = 0; i < raw.length; i++) key[i] = raw.charCodeAt(i);
                pushSub = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: key,
                });
            }
            const json = pushSub.toJSON();
            await axios.post(`${API}/notifications/subscribe`, { endpoint: json.endpoint, keys: json.keys });

            setIsSubscribed(true);
            toast.success(t.subscribed);

            // Trigger a real test push so the user immediately sees confirmation
            try { await axios.post(`${API}/notifications/test?language=${language}`); } catch (e) { /* ignore */ }
        } catch (error) {
            console.error('Push subscription error:', error);
            toast.error(language === 'de' ? 'Fehler beim Aktivieren' : 'Error enabling notifications');
        } finally {
            setIsLoading(false);
        }
    };
    
    const unsubscribe = async () => {
        setIsLoading(true);
        try {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg) {
                const sub = await reg.pushManager.getSubscription();
                if (sub) await sub.unsubscribe();
            }
            await axios.delete(`${API}/notifications/unsubscribe`);
            setIsSubscribed(false);
            toast.success(t.unsubscribed);
        } catch (error) {
            console.error('Unsubscribe error:', error);
        } finally {
            setIsLoading(false);
        }
    };
    
    const categoryConfig = [
        { key: 'motivation', icon: Sparkles, color: 'text-yellow-500' },
        { key: 'reminders', icon: Clock, color: 'text-blue-500' },
        { key: 'achievements', icon: Heart, color: 'text-pink-500' },
        { key: 'health', icon: Bell, color: 'text-green-500' }
    ];
    
    if (!isSupported) {
        return (
            <Card className="bg-secondary/30">
                <CardContent className="p-6">
                    <div className="text-center mb-4">
                        <BellOff className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground font-medium">{t.notSupported}</p>
                    </div>
                    
                    {isIOS && !isStandalone && (
                        <div className="mt-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">
                                📱 {t.notSupportedIOS}
                            </p>
                            <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                                <p>{t.iosStep1}</p>
                                <p>{t.iosStep2}</p>
                                <p>{t.iosStep3}</p>
                            </div>
                            <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                    💡 Nach der Installation kannst du Push-Benachrichtigungen aktivieren!
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {!isIOS && (
                        <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                                💡 Tipp: Verwende Chrome, Edge oder Firefox für Push-Benachrichtigungen.
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }
    
    return (
        <Card data-testid="push-notifications-card">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                            {isSubscribed ? (
                                <BellRing className="w-5 h-5 text-cyan-500" />
                            ) : (
                                <Bell className="w-5 h-5 text-muted-foreground" />
                            )}
                        </div>
                        <div>
                            <CardTitle className="text-lg">{t.title}</CardTitle>
                            <p className="text-sm text-muted-foreground">{t.subtitle}</p>
                        </div>
                    </div>
                    <Badge variant={isSubscribed ? 'default' : 'secondary'}>
                        {isSubscribed ? t.enabled : t.disabled}
                    </Badge>
                </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
                {/* Nanouk Message */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={NANOUK_HAPPY} alt="Nanouk" className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-cyan-600 dark:text-cyan-400 italic">
                        "{t.nanoukSays}"
                    </p>
                </div>
                
                {/* Main Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/50">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isSubscribed ? 'bg-green-500/20' : 'bg-muted'
                        }`}>
                            {isSubscribed ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                            ) : (
                                <XCircle className="w-5 h-5 text-muted-foreground" />
                            )}
                        </div>
                        <span className="font-medium">
                            {isSubscribed ? t.enabled : t.disabled}
                        </span>
                    </div>
                    <Button
                        onClick={handleToggle}
                        disabled={isLoading}
                        variant={isSubscribed ? 'outline' : 'default'}
                        data-testid="push-toggle-btn"
                    >
                        {isLoading ? '...' : (isSubscribed ? t.disable : t.enable)}
                    </Button>
                </div>
                
                {/* Category Settings */}
                {isSubscribed && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="space-y-3"
                    >
                        <h4 className="font-medium text-sm text-muted-foreground">
                            {t.categories.title}
                        </h4>
                        <div className="space-y-2">
                            {categoryConfig.map((cat) => (
                                <div 
                                    key={cat.key}
                                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                                >
                                    <div className="flex items-center gap-3">
                                        <cat.icon className={`w-5 h-5 ${cat.color}`} />
                                        <div>
                                            <p className="font-medium text-sm">
                                                {t.categories[cat.key].title}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {t.categories[cat.key].desc}
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={categories[cat.key]}
                                        onCheckedChange={(checked) => 
                                            setCategories(prev => ({ ...prev, [cat.key]: checked }))
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
                
                {/* Permission Denied Warning */}
                {permission === 'denied' && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                        <p className="text-sm text-red-500">{t.permissionDenied}</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
